// location-data-parser.ts - Updated for new Google Places API and enhanced country support
import { Injectable, Inject, Optional, Injector, APP_INITIALIZER, Provider } from '@angular/core';
import { AppLocation, APILocationResponse } from './location.service';
import { ActivityLogService } from './activity-log';
import { LanguageDetectorService } from './language-detector';

@Injectable({
  providedIn: 'root'
})
export class LocationDataParser {
  private static activityLogService: ActivityLogService | null = null;
  private static languageDetectorService: LanguageDetectorService | null = null;
  private static initialized = false;

  constructor(
    private _activityLogService: ActivityLogService,
    private _languageDetectorService: LanguageDetectorService
  ) {
    // Store static references to services
    LocationDataParser.activityLogService = _activityLogService;
    LocationDataParser.languageDetectorService = _languageDetectorService;
    LocationDataParser.initialized = true;

    // Log initialization
    if (_activityLogService) {
      _activityLogService.logEvent(
        'LocationDataParserInitialized',
        'service_lifecycle'
      );
    }
  }

  /**
   * Converts API location format to app format with enhanced country support
   * @param apiLocation The location data from the API
   * @returns Formatted AppLocation object
   */
  static toAppLocation(apiLocation: APILocationResponse): AppLocation {
    if (!apiLocation) {
      this.logValidationIssue('NullApiLocation', null);
      return this.createEmptyLocation();
    }

    // Get coordinates from the GeoJSON structure
    let lat = 0, lng = 0;
    if (apiLocation.coordinates?.coordinates?.length === 2) {
      lng = apiLocation.coordinates.coordinates[0];
      lat = apiLocation.coordinates.coordinates[1];
    } else {
      this.logValidationIssue('InvalidCoordinates', apiLocation);
    }

    // Enhanced country extraction with priority order
    const country = this.extractCountryInformation(apiLocation);

    // Enhanced region extraction
    const region = this.extractRegionInformation(apiLocation);

    // Enhanced language extraction
    const languages = this.extractLanguageInformation(apiLocation, country);

    // Enhanced name extraction
    const displayName = this.extractDisplayName(apiLocation, region, country);

    // Build comprehensive formatted address
    const formattedAddress = this.buildFormattedAddress(displayName, region, country);

    const result: AppLocation = {
      id: apiLocation.uuid,
      name: displayName,
      formattedAddress,
      lat,
      lng,
      country: country,
      region: region,
      languages,
      sourceId: apiLocation.source_id || undefined,
      sourceCount: 1, // Default to 1 since API doesn't provide source count
      abbreviation: apiLocation.abbreviation || undefined,
      createdAt: apiLocation.created_at ? new Date(apiLocation.created_at) : undefined,
      updatedAt: apiLocation.updated_at ? new Date(apiLocation.updated_at) : undefined,
      distanceKm: apiLocation.distance_km
    };

    // Log successful conversion
    this.logSuccessfulConversion(apiLocation, result);

    return result;
  }

  /**
   * Enhanced country extraction with multiple fallback strategies
   */
  private static extractCountryInformation(apiLocation: APILocationResponse): string {
    // Priority 1: extracted_country field (highest priority)
    if (apiLocation.extracted_country && this.isValidCountryName(apiLocation.extracted_country)) {
      return this.normalizeCountryName(apiLocation.extracted_country);
    }

    // Priority 2: country field
    if (apiLocation.country && this.isValidCountryName(apiLocation.country)) {
      return this.normalizeCountryName(apiLocation.country);
    }

    // Priority 3: Extract from path structure
    if (apiLocation.path) {
      const countryFromPath = this.extractCountryFromPath(apiLocation.path);
      if (countryFromPath && this.isValidCountryName(countryFromPath)) {
        return this.normalizeCountryName(countryFromPath);
      }
    }

    // Priority 4: Try language detection service if available
    if (LocationDataParser.languageDetectorService && apiLocation.name) {
      try {
        const detectedLanguages = LocationDataParser.languageDetectorService.detectLanguages(apiLocation.name);
        if (detectedLanguages.length > 0) {
          const countryFromLanguage = this.inferCountryFromLanguage(detectedLanguages[0]);
          if (countryFromLanguage) {
            return countryFromLanguage;
          }
        }
      } catch (error) {
        console.warn('Failed to detect country from language:', error);
      }
    }

    return ''; // No country information available
  }

  /**
   * Enhanced region extraction
   */
  private static extractRegionInformation(apiLocation: APILocationResponse): string {
    // Priority 1: extracted_region field
    if (apiLocation.extracted_region && apiLocation.extracted_region.trim()) {
      return apiLocation.extracted_region.trim();
    }

    // Priority 2: Extract from path structure (3rd segment is typically region/state)
    if (apiLocation.path) {
      const pathParts = apiLocation.path.split('/').filter(Boolean);
      if (pathParts.length >= 3 && pathParts[2] && pathParts[2] !== '_') {
        return pathParts[2];
      }
    }

    return '';
  }

  /**
   * Enhanced language extraction with country context
   */
  private static extractLanguageInformation(apiLocation: APILocationResponse, country: string): string[] {
    const languages = new Set<string>();

    // Add language from API language field
    if (apiLocation.language) {
      apiLocation.language.forEach(lang => {
        if (lang && typeof lang === 'string' && lang.trim()) {
          languages.add(lang.trim());
        }
      });
    }

    // Add extracted language
    if (apiLocation.extracted_language) {
      apiLocation.extracted_language.forEach(lang => {
        if (lang && typeof lang === 'string' && lang.trim()) {
          languages.add(lang.trim());
        }
      });
    }

    // Add language from path (1st segment is typically language)
    if (apiLocation.path) {
      const pathParts = apiLocation.path.split('/').filter(Boolean);
      if (pathParts.length >= 1 && pathParts[0] && pathParts[0] !== '_') {
        languages.add(pathParts[0]);
      }
    }

    // Use language detector service with country context
    if (LocationDataParser.languageDetectorService && apiLocation.name) {
      try {
        const detectedLangs = LocationDataParser.languageDetectorService.detectLanguages(
          apiLocation.name,
          country
        );

        if (detectedLangs && detectedLangs.length) {
          detectedLangs.forEach(lang => languages.add(lang));
        }
      } catch (error) {
        console.warn('Failed to detect languages:', error);
      }
    }

    // If no languages detected and we have a country, infer from country
    if (languages.size === 0 && country) {
      const countryLanguages = this.getLanguagesForCountry(country);
      countryLanguages.forEach(lang => languages.add(lang));
    }

    // Fallback to English if still no languages
    const result = Array.from(languages).filter(Boolean);
    return result.length > 0 ? result : ['en'];
  }

  /**
   * Enhanced display name extraction
   */
  private static extractDisplayName(apiLocation: APILocationResponse, region: string, country: string): string {
    // Priority 1: API name field (if meaningful)
    if (apiLocation.name && this.isMeaningfulName(apiLocation.name)) {
      return apiLocation.name.trim();
    }

    // Priority 2: Extract city/locality from path (last segment)
    if (apiLocation.path) {
      const pathParts = apiLocation.path.split('/').filter(Boolean);
      const lastPart = pathParts[pathParts.length - 1];
      if (lastPart && lastPart !== '_' && this.isMeaningfulName(lastPart)) {
        return lastPart;
      }
    }

    // Priority 3: Use region if available
    if (region && this.isMeaningfulName(region)) {
      return region;
    }

    // Priority 4: Use country if available
    if (country && this.isMeaningfulName(country)) {
      return country;
    }

    // Fallback: Use UUID
    return apiLocation.uuid;
  }

  /**
   * Build comprehensive formatted address
   */
  private static buildFormattedAddress(name: string, region: string, country: string): string {
    const parts: string[] = [];

    if (name && this.isMeaningfulName(name)) {
      parts.push(name);
    }

    if (region && this.isMeaningfulName(region) && region !== name) {
      parts.push(region);
    }

    if (country && this.isMeaningfulName(country) && country !== name && country !== region) {
      parts.push(country);
    }

    return parts.length > 0 ? parts.join(', ') : 'Unknown Location';
  }

  /**
   * Utility methods for validation and normalization
   */
  private static isValidCountryName(name: string): boolean {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim();
    return trimmed.length > 0 && trimmed !== '_' && trimmed !== '1' && !/^\d+$/.test(trimmed);
  }

  private static isMeaningfulName(name: string): boolean {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim();
    return trimmed.length > 0 && trimmed !== '_' && trimmed !== '1' && !/^\d+$/.test(trimmed);
  }

  private static normalizeCountryName(country: string): string {
    const countryMap: Record<string, string> = {
      'US': 'United States',
      'USA': 'United States',
      'UK': 'United Kingdom',
      'GB': 'United Kingdom'
    };

    const trimmed = country.trim();
    return countryMap[trimmed] || trimmed;
  }

  private static extractCountryFromPath(path: string): string {
    // Path format is typically: language/country/region/city
    const parts = path.split('/').filter(Boolean);
    return parts.length >= 2 ? parts[1] : '';
  }

  private static inferCountryFromLanguage(language: string): string {
    const languageToCountry: Record<string, string> = {
      'en': 'United States',
      'es': 'Spain',
      'fr': 'France',
      'de': 'Germany',
      'it': 'Italy',
      'pt': 'Portugal',
      'ja': 'Japan',
      'ko': 'South Korea',
      'zh': 'China',
      'ru': 'Russia',
      'ar': 'Saudi Arabia'
    };

    return languageToCountry[language] || '';
  }

  private static getLanguagesForCountry(country: string): string[] {
    const countryToLanguages: Record<string, string[]> = {
      'United States': ['en'],
      'United Kingdom': ['en'],
      'Canada': ['en', 'fr'],
      'Mexico': ['es'],
      'Spain': ['es'],
      'France': ['fr'],
      'Germany': ['de'],
      'Italy': ['it'],
      'Portugal': ['pt'],
      'Brazil': ['pt'],
      'Japan': ['ja'],
      'South Korea': ['ko'],
      'China': ['zh'],
      'Russia': ['ru'],
      'India': ['hi', 'en'],
      'Australia': ['en']
    };

    return countryToLanguages[country] || ['en'];
  }

  /**
   * Convert a batch of API locations to app format
   * @param apiLocations Array of location data from the API
   * @returns Array of formatted AppLocation objects
   */
  static toAppLocations(apiLocations: APILocationResponse[]): AppLocation[] {
    if (!apiLocations || !Array.isArray(apiLocations)) {
      this.logValidationIssue('InvalidLocationsArray', apiLocations);
      return [];
    }

    const validLocations = apiLocations
      .map(loc => this.toAppLocation(loc))
      .filter(loc => this.isValidLocation(loc));

    // Log batch processing results
    if (LocationDataParser.activityLogService) {
      LocationDataParser.activityLogService.logEvent(
        'LocationsBatchProcessed',
        'data_transformation',
        {
          totalLocations: apiLocations.length,
          validLocations: validLocations.length,
          invalidLocations: apiLocations.length - validLocations.length,
          countriesFound: new Set(validLocations.map(loc => loc.country).filter(Boolean)).size,
          languagesFound: new Set(validLocations.flatMap(loc => loc.languages)).size
        }
      );
    }

    return validLocations;
  }

  /**
   * Validates a location object
   * @param location Location to validate
   * @returns True if location is valid, false otherwise
   */
  static isValidLocation(location: AppLocation): boolean {
    if (!location) {
      this.logValidationIssue('NullLocation', null);
      return false;
    }

    if (typeof location.name !== 'string' || !location.name.trim()) {
      this.logValidationIssue('MissingName', location);
      return false;
    }

    // Check for valid coordinates
    if (typeof location.lat !== 'number' || typeof location.lng !== 'number') {
      this.logValidationIssue('InvalidCoordinateTypes', location);
      return false;
    }

    // Check coordinate ranges
    if (Math.abs(location.lat) > 90 || Math.abs(location.lng) > 180) {
      this.logValidationIssue('CoordinatesOutOfRange', location);
      return false;
    }

    // Check for valid ID
    if (!location.id) {
      this.logValidationIssue('MissingId', location);
      return false;
    }

    return true;
  }

  /**
   * Creates an empty location object for fallback/error cases
   * @returns Empty AppLocation object
   */
  private static createEmptyLocation(): AppLocation {
    return {
      id: '',
      name: '',
      formattedAddress: '',
      lat: 0,
      lng: 0,
      country: '',
      languages: ['en']
    };
  }

  // Logging methods
  private static logValidationIssue(issueType: string, data: any): void {
    if (LocationDataParser.activityLogService) {
      LocationDataParser.activityLogService.logEvent(
        'LocationValidationIssue',
        'data_validation',
        {
          issueType,
          locationId: data?.uuid || data?.id || 'unknown',
          locationName: data?.name || 'unknown',
          country: data?.country || data?.extracted_country || 'unknown',
          rawData: data ? JSON.stringify(data).substring(0, 200) : 'null'
        }
      );
    } else {
      console.warn(`LocationDataParser validation issue: ${issueType}`);
    }
  }

  private static logSuccessfulConversion(apiLocation: APILocationResponse, result: AppLocation): void {
    if (LocationDataParser.activityLogService) {
      LocationDataParser.activityLogService.logEvent(
        'LocationParsingSucceeded',
        'data_transformation',
        {
          locationId: apiLocation?.uuid || 'unknown',
          locationName: result.name,
          country: result.country,
          region: result.region,
          hasCoordinates: !!result.lat && !!result.lng,
          languagesCount: result.languages?.length || 0,
          extractedFrom: {
            hasExtractedCountry: !!apiLocation?.extracted_country,
            hasExtractedRegion: !!apiLocation?.extracted_region,
            hasExtractedLanguage: !!apiLocation?.extracted_language,
            hasPath: !!apiLocation?.path
          }
        }
      );
    }
  }
}

// Create a provider to ensure LocationDataParser is initialized early
export function initializeLocationDataParser(parser: LocationDataParser): () => void {
  return () => {
    // Just ensure the parser is instantiated
    console.log('LocationDataParser initialized with enhanced country support');
  };
}

export const LOCATION_DATA_PARSER_PROVIDER: Provider = {
  provide: APP_INITIALIZER,
  useFactory: initializeLocationDataParser,
  deps: [LocationDataParser],
  multi: true
};